/**
 * Grandstream call rows (core_event_calls): what a call's outcome columns mean.
 *
 * ONE place for the browser's reading of a call, used by the Event Profile and the call
 * lists. The SQL twin is scripts/gen_time_to_connect_procs.py (ANSWERED / PLACED /
 * ANSWER_TIME); keep the two in step (docs/TIME_TO_CONNECT_PLAN.md, phase 4).
 *
 *   call_outcome     the call's status: answered, no_answer, busy, failed, not_placed
 *   call_end_reason  why it ended, mostly for calls that rang but weren't answered
 *   call_length      call_start_time to hang-up, ringing included ("HH:MM:SS")
 *   talk_length      time after the answer; 00:00:00 unless answered
 *
 * Calls logged before those columns existed have call_outcome NULL. They keep the 4.3.10
 * rule (call_length > 0 = placed and answered, no ring time known), so history reads as
 * it did (user, 2026-10-16).
 *
 * The value lists are the ones seen so far; the full lists come later (user, 2026-10-16).
 * Any value not listed shows as its own text, tidied, so a new one is never blank.
 */

/** Seconds in a "HH:MM:SS" / "MM:SS" / seconds length; null when unparseable. */
export function callSeconds(val) {
    if (val == null || val === '') return null;
    const parts = String(val).split(':').map((p) => Number(p));
    if (parts.some((n) => Number.isNaN(n))) return null;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0];
}

/* Only NULL means "logged before the outcome columns": exactly the SQL's
   `call_outcome IS NULL`, so an empty string is an outcome (none known) on both sides. */
const outcomeOf = (call) => {
    const v = call?.call_outcome;
    return v == null ? null : String(v).toLowerCase();
};

/** Outcomes that reached the callee (Intercom Usage). An allowlist: failed / not_placed don't. */
export const PLACED_OUTCOMES = new Set(['answered', 'no_answer', 'busy']);

/** Was the call answered? Legacy rows (no outcome): length > 0. */
export function isAnsweredCall(call) {
    const outcome = outcomeOf(call);
    if (outcome == null) return callSeconds(call?.call_length) > 0;
    return outcome === 'answered';
}

/** Did the call reach the callee (answered, no answer or busy)? Legacy rows: length > 0. */
export function isPlacedCall(call) {
    const outcome = outcomeOf(call);
    if (outcome == null) return callSeconds(call?.call_length) > 0;
    return PLACED_OUTCOMES.has(outcome);
}

/**
 * Seconds it rang before the answer: call_length - talk_length, never negative. 0 for a
 * legacy row (unknown) or a missing talk_length, so the answer lands on the start time.
 */
export function ringSeconds(call) {
    if (outcomeOf(call) == null) return 0;
    const total = callSeconds(call?.call_length);
    const talk = callSeconds(call?.talk_length);
    if (total == null || talk == null) return 0;
    return Math.max(total - talk, 0);
}

const OUTCOME_LABELS = {
    answered: 'Answered',
    no_answer: 'No answer',
    busy: 'Busy',
    failed: 'Failed',
    not_placed: 'Not placed',
};

const END_REASON_LABELS = {
    caller_cancelled: 'Caller hung up before an answer',
    callee_ring_timeout: 'Rang with no answer',
    sip_486: 'Busy (SIP 486)',
    wake_queue_timeout: 'Device did not wake in time',
    not_registered: 'Device not registered with the phone system',
};

/** "some_value" -> "Some value": the fallback for a value not listed yet. */
const tidy = (v) => {
    const s = String(v).replace(/_/g, ' ').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

/** Display label for call_outcome; '' for a legacy row (no outcome recorded). */
export function callOutcomeLabel(call) {
    const outcome = outcomeOf(call);
    if (outcome == null) return '';
    return OUTCOME_LABELS[outcome] || tidy(outcome);
}

/** Display label for call_end_reason; '' when there is none. */
export function callEndReasonLabel(call) {
    const v = call?.call_end_reason;
    if (v == null || v === '') return '';
    return END_REASON_LABELS[String(v).toLowerCase()] || tidy(v);
}

/**
 * The Talk Time column: talk_length for an answered call, null otherwise. A call logged
 * before talk_length existed has no talk time to show (its Duration still shows).
 */
export function callTalkLength(call) {
    if (outcomeOf(call) == null || !isAnsweredCall(call)) return null;
    return callSeconds(call?.talk_length) != null ? call.talk_length : null;
}

/**
 * Seconds of conversation, for totals: talk_length when outcomes are recorded (0 unless
 * answered); a legacy row's call_length, which is what the totals counted before
 * talk_length existed, so a date range spanning the change stays continuous.
 */
export function callTalkSeconds(call) {
    if (outcomeOf(call) == null) return callSeconds(call?.call_length) || 0;
    return isAnsweredCall(call) ? (callSeconds(call?.talk_length) || 0) : 0;
}

/**
 * Call Logs report summary, from the rows the report lists (every call type).
 *   answerRate   answered / placed over calls WITH a recorded outcome, 0-100, or null
 *                when none was placed. Legacy rows are left out: their outcome is unknown.
 *   totalTalkSeconds / avgTalkSeconds   callTalkSeconds; the average is over answered calls.
 *   byOutcome    [{ name, count }] by outcome label, legacy rows as "Not recorded".
 * The scheduled report computes the same in report_generator.callLogsStats.
 */
export function callLogStats(rows) {
    const list = Array.isArray(rows) ? rows : [];
    let placed = 0, answered = 0, answeredAll = 0, totalTalk = 0, answeredTalk = 0;
    const byOutcome = new Map();
    list.forEach((r) => {
        const talk = callTalkSeconds(r);
        totalTalk += talk;
        if (isAnsweredCall(r)) { answeredAll += 1; answeredTalk += talk; }
        if (outcomeOf(r) != null) {
            if (isPlacedCall(r)) placed += 1;
            if (isAnsweredCall(r)) answered += 1;
        }
        const name = callOutcomeLabel(r) || 'Not recorded';
        byOutcome.set(name, (byOutcome.get(name) || 0) + 1);
    });
    return {
        total: list.length,
        placed,
        answered,
        answerRate: placed > 0 ? (answered / placed) * 100 : null,
        totalTalkSeconds: totalTalk,
        avgTalkSeconds: answeredAll > 0 ? answeredTalk / answeredAll : null,
        byOutcome: [...byOutcome.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count),
    };
}

/**
 * How to colour an outcome: 'answered', 'missed' (placed, not answered: no_answer / busy),
 * 'failed' (never reached the callee, or a value not known yet), or '' for a legacy row.
 */
export function callOutcomeTone(call) {
    const outcome = outcomeOf(call);
    if (outcome == null) return '';
    if (outcome === 'answered') return 'answered';
    if (PLACED_OUTCOMES.has(outcome)) return 'missed';
    return 'failed';
}
